import { classNames } from "primereact/utils";

import CloseIcon from "@/assets/icons/export-close.svg?react";
import { Dialog } from "../dialog";
import { DialogFooter } from "../dialog-footer";
import { Icon } from "../icon";
import { SelectableCard } from "../selectable-card";
import styles from "./export-table-dialog.module.scss";
import {
  EXPORT_TABLE_CLOSE_LABEL,
  EXPORT_TABLE_CONFIRM_LABEL,
  EXPORT_TABLE_SECTION_TITLE,
  EXPORT_TABLE_TITLE,
  EXPORT_TABLE_XLSX_LAYOUT_TITLE,
} from "./messages";
import type { ExportTableDialogViewProps, ExportTableOption } from "./types-export-table-dialog";

function OptionCard({
  option,
  name,
  className,
}: {
  option: ExportTableOption;
  name: string;
  className?: string;
}) {
  return (
    <SelectableCard
      control="none"
      id={option.id}
      inputTestId={option.testId}
      name={name}
      value={option.value}
      selected={option.selected}
      disabled={option.disabled}
      onSelect={option.choose}
      aria-label={option.ariaLabel}
      className={classNames(styles.option, className)}
    >
      {option.iconName ? (
        <span className={styles.badge} aria-hidden>
          <Icon name={option.iconName} />
        </span>
      ) : null}
      <span className={styles.copy}>
        <span className={styles.label}>{option.label}</span>
        <span className={styles.description}>{option.description}</span>
      </span>
    </SelectableCard>
  );
}

export function ExportTableDialogView({
  rest,
  className,
  style,
  classNames: parts,
  isExporting,
  dismissableMask,
  exportDisabled,
  onCancel,
  submit,
  formId,
  formatsTitleId,
  layoutsTitleId,
  testId,
  dialogTestId,
  cancelTestId,
  submitTestId,
  scopeTestId,
  scopeText,
  formats,
  layouts,
}: ExportTableDialogViewProps) {
  return (
    <Dialog
      visible
      size="md"
      onHide={onCancel}
      locked={isExporting}
      dismissableMask={dismissableMask}
      closeIcon={<CloseIcon aria-hidden />}
      ariaCloseIconLabel={EXPORT_TABLE_CLOSE_LABEL}
      header={<span className={parts?.header}>{EXPORT_TABLE_TITLE}</span>}
      contentClassName={parts?.body}
      footer={
        <DialogFooter
          testId={testId}
          cancelTestId={cancelTestId}
          confirmTestId={submitTestId}
          formId={formId}
          confirmLabel={EXPORT_TABLE_CONFIRM_LABEL}
          locked={isExporting}
          loading={isExporting}
          onCancel={onCancel}
          className={parts?.footer}
          cancelClassName={parts?.cancel}
          confirmClassName={classNames(styles.confirm, parts?.confirm)}
          confirmButtonProps={{
            icon: <Icon name="download" />,
            disabled: exportDisabled,
          }}
        />
      }
      data-testid={dialogTestId}
      className={classNames(styles.root, className)}
      style={style}
      {...rest}
    >
      <form id={formId} className={styles.form} onSubmit={submit}>
        <h3 id={formatsTitleId} className={classNames(styles.sectionTitle, parts?.sectionTitle)}>
          {EXPORT_TABLE_SECTION_TITLE}
        </h3>
        <div
          className={classNames(styles.options, parts?.formats)}
          role="radiogroup"
          aria-labelledby={formatsTitleId}
        >
          {formats.map((option) => (
            <OptionCard
              key={option.value}
              option={option}
              name="export-format"
              className={parts?.format}
            />
          ))}
        </div>

        {layouts ? (
          <>
            <h3
              id={layoutsTitleId}
              className={classNames(styles.sectionTitle, parts?.sectionTitle)}
            >
              {EXPORT_TABLE_XLSX_LAYOUT_TITLE}
            </h3>
            <div
              className={classNames(styles.options, parts?.formats)}
              role="radiogroup"
              aria-labelledby={layoutsTitleId}
            >
              {layouts.map((option) => (
                <OptionCard
                  key={option.value}
                  option={option}
                  name="export-xlsx-layout"
                  className={parts?.format}
                />
              ))}
            </div>
          </>
        ) : null}

        <span className={styles.srOnly} data-testid={scopeTestId}>
          {scopeText}
        </span>
      </form>
    </Dialog>
  );
}
